import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { useCustomList } from "store/selectors/bridge";
import {
    customListKeyForEntityType,
    entityTypeNoun,
    findInactiveClientDays,
    getWorkflowStatusIds,
    hasAddedToOrganizationTrigger,
    hasCareStartTrigger,
    hasCheckLaterTrigger,
    hasClientStatusTrigger,
    hasEmailOpenedTrigger,
    hasFieldMatcher,
    hasInactiveClientTrigger,
    hasMoveInTrigger,
    hasReferralSourceTrigger,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowTriggerSummaryProps {
    workflow: EditableWorkflow;
}

const StatusTagList: React.FC<{
    statusIds: string[];
    attachTo: AdvancedSearchEntityType;
}> = ({ statusIds, attachTo }) => {
    const { getListItemName, status } = useCustomList(
        customListKeyForEntityType(attachTo),
    );

    if (status !== "ready") {
        return <span>...</span>;
    }

    const names = statusIds.map(id => getListItemName(id)).filter(Boolean);

    if (names.length === 0) {
        return <span className="text-gray-500 italic">any status</span>;
    }

    return (
        <span className="inline-flex flex-wrap gap-1">
            {names.map((name, i) => (
                <span
                    key={i}
                    className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {name}
                </span>
            ))}
        </span>
    );
};

const ClientStatusTriggerSummary: React.FC<{
    workflow: EditableWorkflow;
}> = ({ workflow }) => {
    const noun = entityTypeNoun(workflow.attachTo);

    if (hasFieldMatcher(workflow, "chosenCommunityId")) {
        return <span>Runs when a community is chosen for a {noun}</span>;
    }

    return <span>Runs when {noun} status changes</span>;
};

const AddedToOrganizationTriggerSummary: React.FC<{
    workflow: EditableWorkflow;
}> = ({ workflow }) => {
    const noun = entityTypeNoun(workflow.attachTo);

    return <span>Runs when a {noun} is first added to an organization</span>;
};

const EmailOpenedTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const thresholdDays = workflow.trigger?.emailOpened?.activityThresholdDays || 0;
    const noun = entityTypeNoun(workflow.attachTo);

    return (
        <span>
            Runs when a {noun} email is opened
            {thresholdDays > 0 && <> after {thresholdDays} days of inactivity</>}
        </span>
    );
};

const InactiveClientTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const days = findInactiveClientDays(workflow);
    const noun = entityTypeNoun(workflow.attachTo);

    return (
        <span>
            Runs daily for {noun}s who have had no activity for {days}{" "}
            {days === 1 ? "day" : "days"}
        </span>
    );
};

const ReferralSourceTriggerSummary: React.FC = () => {
    return <span>Runs when a referral source is set</span>;
};

const MoveInTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({ workflow }) => {
    const noun = entityTypeNoun(workflow.attachTo);
    return <span>Runs when a move-in date is set for a {noun}</span>;
};

const CareStartTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const alsoRequiresReferralSource = hasFieldMatcher(workflow, "referralContactId");
    return (
        <span>
            Runs when a care start date is set
            {alsoRequiresReferralSource && <> and a referral source is present</>}
        </span>
    );
};

const CheckLaterTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const noun = entityTypeNoun(workflow.attachTo);
    return <span>Runs when a check back later date is set for a {noun}</span>;
};

export const WorkflowTriggerSummary: React.FC<WorkflowTriggerSummaryProps> = ({
    workflow,
}) => {
    if (hasCareStartTrigger(workflow)) {
        return <CareStartTriggerSummary workflow={workflow} />;
    }

    if (hasReferralSourceTrigger(workflow)) {
        return <ReferralSourceTriggerSummary />;
    }

    if (hasMoveInTrigger(workflow)) {
        return <MoveInTriggerSummary workflow={workflow} />;
    }

    if (hasCheckLaterTrigger(workflow)) {
        return <CheckLaterTriggerSummary workflow={workflow} />;
    }

    if (hasAddedToOrganizationTrigger(workflow)) {
        return <AddedToOrganizationTriggerSummary workflow={workflow} />;
    }

    if (hasClientStatusTrigger(workflow)) {
        return <ClientStatusTriggerSummary workflow={workflow} />;
    }

    if (hasEmailOpenedTrigger(workflow)) {
        return <EmailOpenedTriggerSummary workflow={workflow} />;
    }

    if (hasInactiveClientTrigger(workflow)) {
        return <InactiveClientTriggerSummary workflow={workflow} />;
    }

    return <span>Custom</span>;
};

export const WorkflowClientStatusesSummary: React.FC<WorkflowTriggerSummaryProps> = ({
    workflow,
}) => {
    const statusIds = getWorkflowStatusIds(workflow);

    if (statusIds.length === 0) {
        return null;
    }

    return <StatusTagList statusIds={statusIds} attachTo={workflow.attachTo} />;
};
